import { useState } from "react";
import { Gauge, Thermometer, ArrowLeftRight, Percent, Truck, Clock, Calculator, Wrench } from "lucide-react";
import { brl } from "@/lib/format";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

function CalcCard({ icon: Icon, title, desc, children, testid, soon }) {
  return (
    <Card className="border-slate-200 shadow-sm" data-testid={testid}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Icon className="h-4.5 w-4.5 h-5 w-5 text-sky-600" /> {title}
          {soon && <span className="text-[10px] font-semibold bg-amber-100 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5">Em desenvolvimento</span>}
        </CardTitle>
        {desc && <p className="text-xs text-slate-500">{desc}</p>}
      </CardHeader>
      <CardContent>{soon ? <p className="text-sm text-slate-400">Módulo técnico em validação com engenharia. Em breve.</p> : children}</CardContent>
    </Card>
  );
}

function Num({ label, value, onChange, testid }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Input className="h-11 font-mono-num" type="number" step="any" data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

export default function Tools() {
  const [mg, setMg] = useState({ custo: 100, markup: 50 });
  const [mg2, setMg2] = useState({ custo: 100, margem: 30 });
  const [desl, setDesl] = useState({ km: 20, valorKm: 2.5, minimo: 50 });
  const [hora, setHora] = useState({ salario: 4000, horasMes: 176, custosFixos: 1500, margem: 30 });
  const [temp, setTemp] = useState({ c: 25 });
  const [press, setPress] = useState({ psi: 100 });
  const [btu, setBtu] = useState({ btuh: 12000 });

  const precoMarkup = (Number(mg.custo) || 0) * (1 + (Number(mg.markup) || 0) / 100);
  const margemDeMarkup = mg.markup > 0 ? (mg.markup / (100 + Number(mg.markup))) * 100 : 0;
  const precoMargem = (Number(mg2.custo) || 0) / (1 - Math.min(Number(mg2.margem) || 0, 95) / 100);
  const custoDesl = Math.max((Number(desl.km) || 0) * (Number(desl.valorKm) || 0), Number(desl.km) > 0 ? Number(desl.minimo) || 0 : 0);
  const custoHora = ((Number(hora.salario) || 0) + (Number(hora.custosFixos) || 0)) / (Number(hora.horasMes) || 1);
  const horaVenda = custoHora / (1 - Math.min(Number(hora.margem) || 0, 95) / 100);

  const c = Number(temp.c) || 0;
  const psi = Number(press.psi) || 0;
  const b = Number(btu.btuh) || 0;

  return (
    <div className="space-y-5 animate-in-up" data-testid="tools-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Ferramentas técnicas</h1>
        <p className="text-sm text-slate-500">Calculadoras e conversores do dia a dia</p>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        <CalcCard icon={Percent} title="Markup → preço" desc="Preço = custo × (1 + markup)" testid="tool-markup">
          <div className="grid grid-cols-2 gap-2">
            <Num label="Custo (R$)" value={mg.custo} onChange={(v) => setMg({ ...mg, custo: v })} testid="markup-custo" />
            <Num label="Markup (%)" value={mg.markup} onChange={(v) => setMg({ ...mg, markup: v })} testid="markup-pct" />
          </div>
          <p className="mt-3 text-sm">Preço: <b className="font-mono-num">{brl(precoMarkup)}</b></p>
          <p className="text-xs text-slate-500">Equivale a margem de {margemDeMarkup.toFixed(1)}%</p>
        </CalcCard>

        <CalcCard icon={Percent} title="Margem → preço" desc="Preço = custo ÷ (1 − margem)" testid="tool-margem">
          <div className="grid grid-cols-2 gap-2">
            <Num label="Custo (R$)" value={mg2.custo} onChange={(v) => setMg2({ ...mg2, custo: v })} testid="margem-custo" />
            <Num label="Margem desejada (%)" value={mg2.margem} onChange={(v) => setMg2({ ...mg2, margem: v })} testid="margem-pct" />
          </div>
          <p className="mt-3 text-sm">Preço: <b className="font-mono-num">{brl(precoMargem)}</b></p>
          <p className="text-xs text-slate-500">Lucro: {brl(precoMargem - (Number(mg2.custo) || 0))}</p>
        </CalcCard>

        <CalcCard icon={Truck} title="Deslocamento" desc="Km total × valor/km, com mínimo" testid="tool-deslocamento">
          <div className="grid grid-cols-3 gap-2">
            <Num label="Km" value={desl.km} onChange={(v) => setDesl({ ...desl, km: v })} testid="desl-km" />
            <Num label="R$/km" value={desl.valorKm} onChange={(v) => setDesl({ ...desl, valorKm: v })} testid="desl-valor" />
            <Num label="Mínimo" value={desl.minimo} onChange={(v) => setDesl({ ...desl, minimo: v })} testid="desl-min" />
          </div>
          <p className="mt-3 text-sm">Cobrar: <b className="font-mono-num">{brl(custoDesl)}</b></p>
        </CalcCard>

        <CalcCard icon={Clock} title="Custo da hora técnica" desc="(salário + custos fixos) ÷ horas produtivas" testid="tool-hora">
          <div className="grid grid-cols-2 gap-2">
            <Num label="Salário alvo (R$/mês)" value={hora.salario} onChange={(v) => setHora({ ...hora, salario: v })} testid="hora-salario" />
            <Num label="Horas produtivas/mês" value={hora.horasMes} onChange={(v) => setHora({ ...hora, horasMes: v })} testid="hora-horas" />
            <Num label="Custos fixos (R$/mês)" value={hora.custosFixos} onChange={(v) => setHora({ ...hora, custosFixos: v })} testid="hora-custos" />
            <Num label="Margem (%)" value={hora.margem} onChange={(v) => setHora({ ...hora, margem: v })} testid="hora-margem" />
          </div>
          <p className="mt-3 text-sm">Custo/hora: <b className="font-mono-num">{brl(custoHora)}</b></p>
          <p className="text-xs text-slate-500">Hora de venda sugerida: {brl(horaVenda)}</p>
        </CalcCard>

        <CalcCard icon={Thermometer} title="Conversor de temperatura" testid="tool-temperatura">
          <Num label="Celsius (°C)" value={temp.c} onChange={(v) => setTemp({ c: v })} testid="temp-c" />
          <div className="mt-3 text-sm space-y-1 font-mono-num">
            <p>{(c * 9 / 5 + 32).toFixed(1)} °F</p>
            <p>{(c + 273.15).toFixed(2)} K</p>
          </div>
        </CalcCard>

        <CalcCard icon={Gauge} title="Conversor de pressão" testid="tool-pressao">
          <Num label="psi" value={press.psi} onChange={(v) => setPress({ psi: v })} testid="press-psi" />
          <div className="mt-3 text-sm space-y-1 font-mono-num">
            <p>{(psi * 0.0689476).toFixed(3)} bar</p>
            <p>{(psi * 6.89476).toFixed(1)} kPa</p>
            <p>{(psi * 0.070307).toFixed(3)} kgf/cm²</p>
          </div>
        </CalcCard>

        <CalcCard icon={ArrowLeftRight} title="Conversor de capacidade" testid="tool-capacidade">
          <Num label="BTU/h" value={btu.btuh} onChange={(v) => setBtu({ btuh: v })} testid="cap-btu" />
          <div className="mt-3 text-sm space-y-1 font-mono-num">
            <p>{(b * 0.000293071).toFixed(3)} kW</p>
            <p>{(b / 12000).toFixed(2)} TR</p>
            <p>{(b * 0.252).toFixed(0)} kcal/h</p>
          </div>
        </CalcCard>

        <CalcCard icon={Calculator} title="Carga de refrigerante (estimativa)" testid="tool-carga" soon>
          {null}
        </CalcCard>

        <CalcCard icon={Wrench} title="Dimensionamento de tubulação" testid="tool-tubulacao" soon>
          {null}
        </CalcCard>
      </div>
    </div>
  );
}
